import { allTracks, formatDuration, framesToSeconds, sequenceDuration, type Project } from '@ve/core';
import { exportVideo } from '@ve/engine';
import { useRef, useState } from 'react';
import { Dialog } from '../../components/Dialog.tsx';
import { useEditor } from '../../state/editor-store.ts';
import { mediaService } from '../../media/setup.ts';
import { useMediaRuntime } from '../../media/service.ts';
import { transport } from '../../state/playback.ts';
import { errorMessage, toast } from '../../state/toast-store.ts';
import { tip } from '../../lib/tooltip.ts';
import { exportGate } from '../../billing/setup.ts';
import { EXPORT_TARGETS, type ExportTarget } from '../../export/targets.ts';

const QUALITY = {
  high: { label: 'High', bitsPerPixel: 0.2 },
  medium: { label: 'Medium', bitsPerPixel: 0.1 },
  low: { label: 'Small file', bitsPerPixel: 0.05 },
} as const;
type Quality = keyof typeof QUALITY;

type Phase =
  | { kind: 'idle' }
  | { kind: 'running'; progress: number; frame: number; startedAt: number }
  | { kind: 'done'; label: string; size: number; getFile(): Promise<File> };

export function ExportDialog({ onClose }: { onClose(): void }) {
  const project = useEditor((s) => s.project)!;
  const activeSeq = useEditor((s) => s.activeSequenceId) ?? project.rootSequenceId;
  const loopRange = useEditor((s) => s.loopRange);
  const [sequenceId, setSequenceId] = useState(project.rootSequenceId);
  const [scale, setScale] = useState(1);
  const [quality, setQuality] = useState<Quality>('high');
  const [codec, setCodec] = useState<'avc' | 'hevc'>('avc');
  const [useRange, setUseRange] = useState(false);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [target, setTarget] = useState<ExportTarget>(EXPORT_TARGETS[0]!);
  const abortRef = useRef<AbortController | null>(null);

  const { width, height, fps } = project.settings;
  const outW = Math.round((width * scale) / 2) * 2;
  const outH = Math.round((height * scale) / 2) * 2;
  const fpsNum = fps.num / fps.den;
  const videoBitrate = Math.round(outW * outH * fpsNum * QUALITY[quality].bitsPerPixel);
  const total = sequenceDuration(project, sequenceId);
  const range = useRange && loopRange ? loopRange : { start: 0, end: total };
  const seconds = framesToSeconds(range.end - range.start, fps);
  const estimateMb = ((videoBitrate + 192_000) * seconds) / 8 / 1e6;

  const runtime = useMediaRuntime((st) => st.runtime);
  const offline = [...mediaUsedBy(project, sequenceId)].filter(
    (id) => runtime[id]?.availability !== 'online',
  );

  const start = async () => {
    if (range.end <= range.start) return toast.warn('Nothing to export — the timeline is empty');
    transport.pause();
    const fileName = `${project.name.replace(/[\\/:*?"<>|]+/g, '').trim() || 'export'}.mp4`;
    // Order matters: pickers need the click's user activation, so open the file first.
    let file;
    try {
      file = await target.open(project.id, fileName);
    } catch (err) {
      return toast.error(`Could not create the output file: ${errorMessage(err)}`);
    }
    if (!file) return; // cancelled
    const request = {
      projectId: project.id,
      projectName: project.name,
      width: outW,
      height: outH,
      durationSeconds: seconds,
    };
    const auth = await exportGate.authorize(request).catch((err: unknown) => ({
      status: 'denied' as const,
      reason: errorMessage(err),
    }));
    if (auth.status !== 'granted') {
      await file.writable.abort().catch(() => undefined);
      if (auth.status === 'denied') toast.warn(auth.reason);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    const startedAt = performance.now();
    setPhase({ kind: 'running', progress: 0, frame: 0, startedAt });
    try {
      const result = await exportVideo({
        destination: { kind: 'stream', writable: file.writable },
        project,
        sequenceId,
        resolveMedia: mediaService.resolve,
        range,
        width: outW,
        height: outH,
        videoBitrate,
        audioBitrate: 192_000,
        codec,
        signal: controller.signal,
        onProgress: (progress, frame) => setPhase({ kind: 'running', progress, frame, startedAt }),
      });
      setPhase({ kind: 'done', label: file.label, size: result.bytesWritten, getFile: file.getFile });
      toast.success(`Saved ${file.label}`);
      void exportGate.completed?.(request, auth.grant);
    } catch (err) {
      void exportGate.failed?.(request, auth.grant);
      if (controller.signal.aborted) {
        toast.info('Export cancelled');
        setPhase({ kind: 'idle' });
      } else {
        toast.error(`Export failed: ${errorMessage(err)}`);
        setPhase({ kind: 'idle' });
      }
    } finally {
      abortRef.current = null;
    }
  };

  const close = () => {
    abortRef.current?.abort();
    onClose();
  };

  /** Opens the finished file in a new tab (the browser's own player). */
  const play = async () => {
    if (phase.kind !== 'done') return;
    const url = URL.createObjectURL(await phase.getFile());
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const running = phase.kind === 'running';
  const eta =
    phase.kind === 'running' && phase.progress > 0.02
      ? ((performance.now() - phase.startedAt) / 1000) * ((1 - phase.progress) / phase.progress)
      : null;

  return (
    <Dialog
      title="Export MP4"
      onClose={close}
      footer={
        phase.kind === 'done' ? (
          <>
            <button
              className="btn"
              onClick={() => void play()}
              {...tip('Watch the exported video in a new tab')}
            >
              Play
            </button>
            <button className="btn primary" onClick={close} {...tip('Close this dialog')}>
              Done
            </button>
          </>
        ) : (
          <>
            <button
              className="btn"
              onClick={running ? () => abortRef.current?.abort() : close}
              {...tip(
                running ? 'Stop the export; nothing is saved' : 'Close without exporting',
                running ? undefined : 'Escape',
              )}
            >
              {running ? 'Cancel export' : 'Cancel'}
            </button>
            <button
              className="btn primary"
              disabled={running}
              onClick={() => void start()}
              {...tip(
                running
                  ? 'Export in progress…'
                  : `Render ${outW}×${outH} and save it to ${target.id === 'project-folder' ? 'the Exports folder' : 'a location you choose'}`,
              )}
            >
              Export{exportGate.priceLabel !== 'Free' ? ` · ${exportGate.priceLabel}` : ''}
            </button>
          </>
        )
      }
    >
      <fieldset className="form-grid export-form" disabled={running || phase.kind === 'done'}>
        <label>Timeline</label>
        <select className="select" value={sequenceId} onChange={(e) => setSequenceId(e.target.value)}>
          {Object.values(project.sequences).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
              {s.id === project.rootSequenceId ? ' (main)' : ''}
              {s.id === activeSeq ? ' — open' : ''}
            </option>
          ))}
        </select>
        <label>Resolution</label>
        <select className="select" value={scale} onChange={(e) => setScale(Number(e.target.value))}>
          <option value={1}>
            {width}×{height} (project)
          </option>
          <option value={0.5}>
            {Math.round(width / 4) * 2}×{Math.round(height / 4) * 2} (half)
          </option>
          {width < 3840 && (
            <option value={2}>
              {width * 2}×{height * 2} (2×)
            </option>
          )}
        </select>
        <label>Quality</label>
        <select className="select" value={quality} onChange={(e) => setQuality(e.target.value as Quality)}>
          {(Object.keys(QUALITY) as Quality[]).map((q) => (
            <option key={q} value={q}>
              {QUALITY[q].label} · {((outW * outH * fpsNum * QUALITY[q].bitsPerPixel) / 1e6).toFixed(1)}{' '}
              Mbit/s
            </option>
          ))}
        </select>
        <label>Codec</label>
        <select className="select" value={codec} onChange={(e) => setCodec(e.target.value as 'avc' | 'hevc')}>
          <option value="avc">H.264 (most compatible)</option>
          <option value="hevc">H.265 / HEVC (smaller)</option>
        </select>
        <label>Range</label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={useRange}
            disabled={!loopRange}
            onChange={(e) => setUseRange(e.target.checked)}
          />
          Only in/out range {loopRange ? '' : '(set with I / O)'}
        </label>
        <label>Save to</label>
        <select
          className="select"
          value={target.id}
          onChange={(e) => setTarget(EXPORT_TARGETS.find((t) => t.id === e.target.value)!)}
          {...tip(
            'Where the MP4 is written. It is streamed straight to disk, so length is not limited by memory.',
          )}
        >
          {EXPORT_TARGETS.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
        <label>Length</label>
        <span className="muted">
          {formatDuration(seconds)} · ≈ {estimateMb.toFixed(0)} MB
        </span>
      </fieldset>
      {offline.length > 0 && (
        <p className="form-error">
          {offline.length} media file(s) used in this timeline are offline and would be missing from the
          export. Reconnect the project folder first.
        </p>
      )}
      {phase.kind === 'running' && (
        <div className="export-progress">
          <div className="progress">
            <div style={{ width: `${(phase.progress * 100).toFixed(1)}%` }} />
          </div>
          <div className="muted">
            {(phase.progress * 100).toFixed(1)}% · frame {phase.frame - range.start} /{' '}
            {range.end - range.start}
            {eta !== null && ` · ~${formatDuration(eta)} left`}
          </div>
        </div>
      )}
      {phase.kind === 'done' && (
        <p>
          Saved <strong>{phase.label}</strong> ({(phase.size / 1e6).toFixed(1)} MB).
        </p>
      )}
    </Dialog>
  );
}

/** Media ids used by a sequence, including inside nested sequences. */
function mediaUsedBy(project: Project, sequenceId: string, seen = new Set<string>()): Set<string> {
  const used = new Set<string>();
  const seq = project.sequences[sequenceId];
  if (!seq || seen.has(sequenceId)) return used;
  seen.add(sequenceId);
  for (const track of allTracks(seq)) {
    for (const clip of track.clips) {
      if ('mediaId' in clip) used.add(clip.mediaId);
      if (clip.type === 'sequence')
        for (const id of mediaUsedBy(project, clip.sequenceId, seen)) used.add(id);
    }
  }
  return used;
}
